import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Plus, Printer, Trash2, RotateCcw, ShieldOff, CircleSlash } from "lucide-react";
import LuggagePrintSheet from "@/components/luggage/LuggagePrintSheet";

const STATUS_TONE = { free: "emerald", occupied: "amber", lost: "red", retired: "slate" };

export default function LuggageLockers({ isManager, branches, refreshKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ branch: "", status: "", q: "" });
  const [selected, setSelected] = useState(new Set());
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ branch: "", prefix: "ABR", start: 1, count: 20, contact_phone: "", contact_email: "" });
  const [genBusy, setGenBusy] = useState(false);
  const [printLockers, setPrintLockers] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.branch) p.append("branch", filters.branch);
      if (filters.status) p.append("status", filters.status);
      if (filters.q) p.append("q", filters.q);
      const { data } = await api.get(`/luggage/lockers?${p.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters, refreshKey]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const generate = async () => {
    if (!gen.branch) { toast.error("Pick a branch"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/luggage/lockers/generate", {
        ...gen, start: Number(gen.start) || 1, count: Number(gen.count) || 1,
      });
      toast.success(`${data.created} card(s) created${data.skipped.length ? `, ${data.skipped.length} already existed` : ""}`);
      setGenOpen(false);
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenBusy(false); }
  };

  const setStatus = async (l, status) => {
    try {
      await api.patch(`/luggage/lockers/${l.id}`, { status });
      toast.success(`${l.locker_no} marked ${status}`);
      onChanged?.(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete card ${l.locker_no} permanently?`)) return;
    try {
      await api.delete(`/luggage/lockers/${l.id}`);
      toast.success(`${l.locker_no} deleted`);
      onChanged?.(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const printSelection = () => {
    const list = selected.size ? items.filter((i) => selected.has(i.id)) : items;
    if (!list.length) { toast.error("No cards to print"); return; }
    setPrintLockers(list);
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={filters.branch} data-testid="lug-filter-branch"
          onChange={(e) => setFilters((f) => ({ ...f, branch: e.target.value }))}
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
        <select value={filters.status} data-testid="lug-filter-status"
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
          <option value="">Any status</option>
          {["free", "occupied", "lost", "retired"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Input placeholder="Search number…" value={filters.q} data-testid="lug-filter-q"
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className="h-9 w-40" />
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={printSelection} data-testid="lug-print-button">
            <Printer size={14} className="mr-1.5" />
            Print cards{selected.size ? ` (${selected.size})` : ""}
          </Button>
          {isManager && (
            <Button size="sm" onClick={() => setGenOpen(true)}
              className="bg-emerald-900 hover:bg-emerald-800" data-testid="lug-generate-button">
              <Plus size={14} className="mr-1.5" /> Generate cards
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading lockers…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No cards yet"
          hint={isManager ? "Click 'Generate cards' to create a numbered batch for a branch (e.g. ABR-001 … ABR-100)." : "Ask a manager to generate this branch's card set."} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="lug-lockers-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-3 py-2.5">Card / Locker</th>
                <th className="px-3 py-2.5">Branch</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Contacts on card</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                  data-testid={`lug-row-${l.locker_no}`}>
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)}
                      className="accent-emerald-800" data-testid={`lug-select-${l.locker_no}`} />
                  </td>
                  <td className="px-3 py-2.5 font-display font-bold text-emerald-900">{l.locker_no}</td>
                  <td className="px-3 py-2.5 text-gray-600">{l.branch}</td>
                  <td className="px-3 py-2.5"><Badge tone={STATUS_TONE[l.status] || "slate"}>{l.status}</Badge></td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">
                    {[l.contact_phone, l.contact_email].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1">
                      {l.status === "lost" && (
                        <button onClick={() => setStatus(l, "free")} title="Restore card"
                          className="p-1.5 rounded text-emerald-700 hover:bg-emerald-50" data-testid={`lug-restore-${l.locker_no}`}>
                          <RotateCcw size={14} />
                        </button>
                      )}
                      {l.status === "free" && (
                        <button onClick={() => setStatus(l, "lost")} title="Mark lost"
                          className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`lug-lost-${l.locker_no}`}>
                          <ShieldOff size={14} />
                        </button>
                      )}
                      {isManager && l.status !== "occupied" && l.status !== "retired" && (
                        <button onClick={() => setStatus(l, "retired")} title="Retire card"
                          className="p-1.5 rounded text-gray-500 hover:bg-gray-100" data-testid={`lug-retire-${l.locker_no}`}>
                          <CircleSlash size={14} />
                        </button>
                      )}
                      {isManager && (
                        <button onClick={() => remove(l)} title="Delete"
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`lug-delete-${l.locker_no}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lug-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate luggage cards</DialogTitle>
            <DialogDescription>
              Creates a numbered batch with unique, unforgeable QR codes for one branch.
              Print each card twice — one for the customer, one stays with the luggage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Branch</Label>
              <select value={gen.branch} data-testid="lug-gen-branch"
                onChange={(e) => setGen((g) => ({ ...g, branch: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm">
                <option value="">Select branch…</option>
                {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Prefix</Label>
                <Input value={gen.prefix} data-testid="lug-gen-prefix"
                  onChange={(e) => setGen((g) => ({ ...g, prefix: e.target.value.toUpperCase() }))}
                  className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">Start №</Label>
                <Input type="number" min={1} value={gen.start} data-testid="lug-gen-start"
                  onChange={(e) => setGen((g) => ({ ...g, start: e.target.value }))} className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">How many</Label>
                <Input type="number" min={1} max={1000} value={gen.count} data-testid="lug-gen-count"
                  onChange={(e) => setGen((g) => ({ ...g, count: e.target.value }))} className="mt-1 h-10" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Contact phone (printed on card)</Label>
              <Input value={gen.contact_phone} placeholder="+256-700-000-000" data-testid="lug-gen-phone"
                onChange={(e) => setGen((g) => ({ ...g, contact_phone: e.target.value }))} className="mt-1 h-10" />
            </div>
            <div>
              <Label className="text-xs">Contact email (printed on card)</Label>
              <Input value={gen.contact_email} placeholder="luggage@abrah.co.ug" data-testid="lug-gen-email"
                onChange={(e) => setGen((g) => ({ ...g, contact_email: e.target.value }))} className="mt-1 h-10" />
            </div>
            <div className="text-xs text-gray-500">
              Will create: <span className="font-mono font-semibold">
                {(gen.prefix || "ABR")}-{String(Number(gen.start) || 1).padStart(3, "0")}
              </span> … <span className="font-mono font-semibold">
                {(gen.prefix || "ABR")}-{String((Number(gen.start) || 1) + (Number(gen.count) || 1) - 1).padStart(3, "0")}
              </span>
            </div>
            <Button onClick={generate} disabled={genBusy}
              className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="lug-gen-submit">
              {genBusy ? "Creating…" : "Create cards"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {printLockers && (
        <LuggagePrintSheet lockers={printLockers} onClose={() => setPrintLockers(null)} />
      )}
    </div>
  );
}
